import {generationInProgress} from '@/service/generationSaveGuard';
import {articleGenerationDone} from '@/service/saveIntegrity';

/** Toast shown when a save is held because AI Lucy is still writing. */
export const SAVE_HELD_TOAST_TITLE = 'article.saveHeld.title';
export const SAVE_HELD_TOAST_CONTENT = 'article.saveHeld.content';
/** Toast shown when a save is held because path B proposals still wait for the user. */
export const SAVE_HELD_REVIEW_TOAST_TITLE = 'article.saveHeldReview.title';
export const SAVE_HELD_REVIEW_TOAST_CONTENT = 'article.saveHeldReview.content';

const SAVE_HELD_TOAST_TITLES = [SAVE_HELD_TOAST_TITLE, SAVE_HELD_REVIEW_TOAST_TITLE];

/**
 * Why generation still holds a save of this article: the run writes it, path B
 * proposals wait for the user, or generation is done and nothing should hold it.
 */
export type SaveHeldState = 'generating' | 'review' | 'settled';

export function saveHeldState(
    article: {enhanced?: string | null; aiGenerateStatus?: string | null} | null | undefined,
): SaveHeldState {
    if (articleGenerationDone(article)) {
        return 'settled';
    }
    if (generationInProgress(article)) {
        return 'generating';
    }
    return article?.aiGenerateStatus === 'review' ? 'review' : 'generating';
}

/**
 * A held save is never silent. While generation runs it is the expected
 * still-writing note. After done the editor expects a save, so the same text
 * is an error. During path B review nothing is writing: until the user decides
 * every proposal, a filled section or FAQ cannot be cleared or renamed, so the
 * error tells them to go through the proposals first. The caller does not
 * navigate away after any of them.
 */
export function saveHeldToast(state: SaveHeldState): {
    type: 'info' | 'error';
    title: string;
    content: string;
    dismissible: true;
} {
    if (state === 'review') {
        return {
            type: 'error',
            title: SAVE_HELD_REVIEW_TOAST_TITLE,
            content: SAVE_HELD_REVIEW_TOAST_CONTENT,
            dismissible: true,
        };
    }
    return {
        type: state === 'settled' ? 'error' : 'info',
        title: SAVE_HELD_TOAST_TITLE,
        content: SAVE_HELD_TOAST_CONTENT,
        dismissible: true,
    };
}

export function saveHeldToastIds(
    toasts: Array<{id?: number; title?: string}> | null | undefined,
): number[] {
    return (toasts || [])
        .filter((toast) => toast && SAVE_HELD_TOAST_TITLES.includes(toast.title as string) && typeof toast.id === 'number')
        .map((toast) => toast.id as number);
}

export function dismissSaveHeldToasts(
    toasts: Array<{id?: number; title?: string}> | null | undefined,
    remove: (id: number) => void,
): void {
    for (const id of saveHeldToastIds(toasts)) {
        remove(id);
    }
}
